import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { BillDiscountType, ProviderDiscount } from '../../../models/medical-bill';
import { ICategory } from '../../../models/procedure';
import { BillDiscountRequest, PracticeRatesService } from '../../../services/practice-rates.service';

@Component({
    selector: 'app-practice-discount-form',
    standalone: true,
    imports: [FormsModule, NgIf, DialogModule, DropdownModule, InputNumberModule, CheckboxModule, ButtonModule, DividerModule],
    templateUrl: './practice-discount-form.component.html',
})
export class PracticeDiscountFormComponent implements OnInit, OnChanges
{
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() ein = '';
    @Input() practiceName = '';

    @Input() locationId?: number;
    @Input() npi?: string;
    @Input() editing?: ProviderDiscount | null;

    @Output() saved = new EventEmitter<void>();

    saving = false;

    categories: ICategory[] = [];
    categoryId: number | null = null;

    discountType: BillDiscountType = BillDiscountType.DiscountOffOfCharges;
    amount: number = 0;
    replaceExisting: boolean = false;
    extraDiscountSubsequentProcedures: boolean = true;
    subsequentProcedureDiscount: number = 0.5;

    readonly discountTypeOptions = [
        { label: 'Discount off Charges',          value: BillDiscountType.DiscountOffOfCharges },
        { label: 'Flat Amount by Category',       value: BillDiscountType.FlatAmountByCategory },
        { label: 'Flat Amount by Category Unit',  value: BillDiscountType.FlatAmountByCategoryUnit },
        { label: 'Flat Amount per Bill',          value: BillDiscountType.FlatAmountPerBillDiscount },
        { label: 'Flat Amount by Line Item',      value: BillDiscountType.FlatAmountByLineItem },
    ];

    get showAmount(): boolean
    {
        return true;
    }

    get amountIsPercent(): boolean
    {
        return this.discountType === BillDiscountType.DiscountOffOfCharges;
    }

    get requiresCategory(): boolean
    {
        return this.discountType === BillDiscountType.FlatAmountByCategory
            || this.discountType === BillDiscountType.FlatAmountByCategoryUnit;
    }

    get canSave(): boolean
    {
        return !this.requiresCategory || !!this.categoryId;
    }

    get isLocationScoped(): boolean
    {
        return this.locationId != null && !!this.npi;
    }

    get isEditing(): boolean
    {
        return !!this.editing;
    }

    constructor(private _ratesService: PracticeRatesService) {}

    async ngOnInit()
    {
        this.categories = await this._ratesService.getCategories();
    }

    ngOnChanges(changes: SimpleChanges)
    {
        if (changes['editing'] && this.editing)
        {
            this.discountType = this.editing.discountType;
            this.categoryId = this.editing.category?.id ?? null;
            this.amount = this.editing.amount;
        }
        else if (changes['visible'] && this.visible && !this.editing)
        {
            this.discountType = BillDiscountType.DiscountOffOfCharges;
            this.categoryId = null;
            this.amount = 0;
            this.replaceExisting = false;
        }
    }

    async save()
    {
        if (!this.ein || !this.canSave) return;
        this.saving = true;

        const request: BillDiscountRequest = {
            discountType: this.discountType,
            categoryId: this.requiresCategory ? this.categoryId! : undefined,
            amount: this.showAmount ? this.amount : 0,
            replaceExisting: this.isEditing || this.replaceExisting,
            extraDiscountSubsequentProcedures: this.extraDiscountSubsequentProcedures,
            subsequentProcedureDiscount: this.subsequentProcedureDiscount,
        };

        const save = this.isLocationScoped
            ? this._ratesService.addLocationDiscount(this.ein, this.locationId!, this.npi!, request)
            : this._ratesService.addPracticeDiscount(this.ein, request);

        await save.finally(() => this.saving = false);

        this.saved.emit();
        this.close();
    }

    close()
    {
        this.visible = false;
        this.visibleChange.emit(false);
    }
}
